import { create } from 'zustand';
import type { AffiliateCsvImportRequest, AffiliateCsvImportStatusResponse } from '@fluenceur/types';
import { buildRowsFromMapping, type ParsedRow } from '@/pages/affiliate-import/_lib/csv-parser';
import type { HeaderMapping } from '@/pages/affiliate-import/_lib/header-aliases';

/** Fields the wire may omit: the initial POST snapshot knows none of them yet. */
type OptionalProgressKeys =
  | 'queued'
  | 'providerAccepted'
  | 'deliveryFailed'
  | 'deliveryUnknown'
  | 'deliveryMode'
  | 'pauseReason'
  | 'resumeAt';

export type ImportBatchProgress = Omit<AffiliateCsvImportStatusResponse, OptionalProgressKeys> &
  Partial<Pick<AffiliateCsvImportStatusResponse, OptionalProgressKeys>>;

export type ImportBatchStatus = ImportBatchProgress['status'];

/**
 * Statuses the worker never leaves. Single source of truth for the polling stop,
 * the store's no-regression guards, the wizard's terminal transition and the
 * background monitor's announcement, which would otherwise drift apart.
 */
const TERMINAL_IMPORT_STATUSES: ReadonlySet<ImportBatchStatus> = new Set([
  'done',
  'error',
  'cancelled',
]);

export const isTerminalImportStatus = (status: ImportBatchStatus | undefined): boolean =>
  status !== undefined && TERMINAL_IMPORT_STATUSES.has(status);

/** Kept CSV-local: no invitation/wishlist targets leak into this wizard state. */
export type CsvImportDelivery =
  | Omit<Extract<AffiliateCsvImportRequest, { deliveryMode: 'link' }>, 'rows'>
  | Omit<Extract<AffiliateCsvImportRequest, { deliveryMode: 'email' }>, 'rows'>;

interface AffiliateCsvImportState {
  fileName: string | null;
  headers: string[];
  headerLabels: string[];
  mapping: HeaderMapping;
  rawRows: Record<string, unknown>[];
  rows: ParsedRow[];
  totalRows: number;
  exceededLimit: boolean;
  isHeaderless: boolean;
  delivery: CsvImportDelivery;

  batch: ImportBatchProgress | null;

  setParsed: (payload: {
    fileName: string;
    headers: string[];
    headerLabels: string[];
    mapping: HeaderMapping;
    rawRows: Record<string, unknown>[];
    rows: ParsedRow[];
    totalRows: number;
    exceededLimit: boolean;
    isHeaderless: boolean;
  }) => void;
  updateMapping: (mapping: HeaderMapping) => void;
  setDelivery: (delivery: CsvImportDelivery) => void;
  setBatch: (batch: ImportBatchProgress | null) => void;
  /**
   * Apply a Pusher progress event. Drops events in three cases:
   *
   * 1. `state.batch` is null (no active import). Pre-mutation race: the very first Pusher
   *    tick can fire before `setBatch` resolves on the POST response. Mitigated by mounting
   *    the Pusher subscription only AFTER `setBatch` (see ImportingStep TODO) plus the
   *    GET `/:batchId` rehydrate path, but the guard stays as defense for hot-reloads or
   *    stale subscription handles.
   *
   * 2. `event.batchId` does not match the active batch. Two simultaneous imports share the
   *    brand-scoped channel; without this filter they would cross-contaminate progress.
   *
   * 3. The active batch already reached a terminal status (`done`, `error` or `cancelled`). A
   *    delayed `running` tick arriving after the worker emitted `done` would otherwise rewind
   *    progress numbers under the user's eyes (and re-trigger ImportingStep's status
   *    watcher into resuming the fake ticker on a page that already moved to `done`).
   */
  applyProgressEvent: (event: ImportBatchProgress) => void;
  reset: () => void;
}

const PROGRESS_KEYS = [
  'batchId',
  'total',
  'processed',
  'succeeded',
  'skipped',
  'failed',
  'queued',
  'providerAccepted',
  'deliveryFailed',
  'deliveryUnknown',
  'errorCode',
  'pauseReason',
  'resumeAt',
  'deliveryMode',
  'status',
] as const satisfies readonly (keyof ImportBatchProgress)[];

/** Field-by-field over the known, flat progress shape: no key-order or serialization traps. */
const isSameProgress = (
  left: ImportBatchProgress | null,
  right: ImportBatchProgress | null
): boolean => {
  if (left === right) return true;
  if (!left || !right) return false;
  return PROGRESS_KEYS.every((key) => left[key] === right[key]);
};

const initialState = {
  fileName: null,
  headers: [],
  headerLabels: [],
  mapping: {},
  rawRows: [],
  rows: [],
  totalRows: 0,
  exceededLimit: false,
  isHeaderless: false,
  delivery: { deliveryMode: 'link' },
  batch: null,
} satisfies Omit<
  AffiliateCsvImportState,
  'setParsed' | 'updateMapping' | 'setDelivery' | 'setBatch' | 'applyProgressEvent' | 'reset'
>;

const useAffiliateCsvImportStore = create<AffiliateCsvImportState>((set) => ({
  ...initialState,

  setParsed: (payload) =>
    set({
      fileName: payload.fileName,
      headers: payload.headers,
      headerLabels: payload.headerLabels,
      mapping: payload.mapping,
      rawRows: payload.rawRows,
      rows: payload.rows,
      totalRows: payload.totalRows,
      exceededLimit: payload.exceededLimit,
      isHeaderless: payload.isHeaderless,
      delivery: { deliveryMode: 'link' },
      batch: null,
    }),

  // The first CSV implementation only updated the selected header labels, leaving
  // `rows` bound to the old mapping. Rebuild from the retained raw rows so preview
  // and POST payload always reflect the mapping the brand actually confirmed.
  updateMapping: (mapping) =>
    set((state) => ({
      mapping,
      rows: buildRowsFromMapping(state.rawRows, mapping, state.isHeaderless),
    })),

  setDelivery: (delivery) => set({ delivery }),

  // REST may race a terminal Pusher event with one stale `running` response.
  // Never regress the terminal snapshot the user is already looking at.
  setBatch: (batch) =>
    set((state) => {
      if (
        batch &&
        state.batch?.batchId === batch.batchId &&
        isTerminalImportStatus(state.batch.status) &&
        !isTerminalImportStatus(batch.status)
      ) {
        return state;
      }
      // TanStack Query can serve an equivalent object on successive renders.
      // Avoid publishing an identical Zustand snapshot, which would otherwise
      // re-run the DoneStep rehydration effect after a terminal refresh.
      if (isSameProgress(state.batch, batch)) {
        return state;
      }
      return { batch };
    }),

  applyProgressEvent: (event) =>
    set((state) => {
      if (!state.batch || state.batch.batchId !== event.batchId) {
        return state;
      }
      if (isTerminalImportStatus(state.batch.status)) {
        return state;
      }
      return { batch: { ...state.batch, ...event } };
    }),

  reset: () => set({ ...initialState }),
}));

export default useAffiliateCsvImportStore;
